import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetNpsCampaignsQueryOptions,
  useCreateNpsCampaign,
} from '@area-restrita/api-client/endpoints/nps/nps.gen';
import { toast } from 'react-toastify';

import DataGrid from '@/components/DataGrid';
import Dialog from '@/components/template/Dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useQueryTable } from '@/hooks/useQueryTable';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import NpsCampaignFilters from '@/modules/nps/components/NpsCampaignFilters';
import NpsCampaignForm from '@/modules/nps/components/NpsCampaignForm';
import { NpsCampaignFormSchema } from '@/modules/nps/components/NpsCampaignForm/schema';
import { getErrorMessage, toIsoDateTime } from '@/modules/nps/utils';

import { columns } from './columns';

const NpsCampaignsTable: React.FC = () => {
  const queryClient = useQueryClient();
  const { checkAuthorization } = useAuthSessionStore();
  const canCreate = checkAuthorization(({ can }) => can('create', 'nps'));
  const createMutation = useCreateNpsCampaign();

  const {
    query: { isError, isLoading, error },
    table,
  } = useQueryTable({
    columns,
    getQueryOptions: getGetNpsCampaignsQueryOptions,
  });

  const handleCreate = async (data: NpsCampaignFormSchema) => {
    await createMutation.mutateAsync(
      {
        data: {
          ...data,
          startAt: toIsoDateTime(data.startAt),
          endAt: toIsoDateTime(data.endAt),
        },
      },
      {
        onSuccess: async () => {
          toast.success('Campanha criada como rascunho.');
          await queryClient.invalidateQueries({
            queryKey: ['/api/v1/nps/campaigns'],
          });
        },
        onError: (mutationError) =>
          toast.error(
            getErrorMessage(mutationError, 'Erro ao criar campanha NPS.'),
          ),
      },
    );
  };

  return (
    <>
      {isError && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Erro ao carregar campanhas</AlertTitle>
          <AlertDescription>
            {getErrorMessage(error, 'Tente novamente em instantes.')}
          </AlertDescription>
        </Alert>
      )}

      <DataGrid
        variant="card"
        table={table}
        isLoading={isLoading}
        toolbar={<NpsCampaignFilters table={table} />}
        classNameActions="grid grid-cols-2 sm:w-full [&_button]:w-full xl:flex xl:w-auto xl:[&_button]:w-auto"
        actions={
          canCreate && (
            <Dialog
              title="Nova campanha NPS"
              description="Crie um rascunho para revisar antes de abrir a coleta."
              trigger={<Button size="sm">Nova campanha</Button>}
            >
              {({ handleClose }) => (
                <NpsCampaignForm
                  isSubmitting={createMutation.isPending}
                  onCancel={handleClose}
                  onSubmit={async (data) => {
                    await handleCreate(data);
                    handleClose();
                  }}
                />
              )}
            </Dialog>
          )
        }
      />
    </>
  );
};

export default NpsCampaignsTable;
